import type { FC } from 'react'
import React, { useCallback, useMemo } from 'react'
import _range from 'lodash/range'
import _uniqBy from 'lodash/uniqBy'
import moment from 'moment'

import { FormBlockWrapper, ButtonWrapper } from './style'
import TimePickerDiffTime from 'components/TimePickerDiffTime'
import SingleForm from 'components/SingleForm'
import { DEFAULT_OTHER_OPTION_SELECT } from 'components/SelectOptions'
import theme from 'vars/theme'

interface EntryInstructionTimeManagementFormProps {
  handleChange?: (val: any, name: string) => void
  handleClearDriverSetting: () => void
  errors?: any
  entryInstructionDateTime?: string
  entryInstructionCombinedTime?: string
  skipInstructionTime?: boolean
  isValidateEntryInstructionTime: {
    entryInstructionDateTime?: boolean
    entryInstructionCombinedTime?: boolean
  }
}

const EntryInstructionTimeManagementForm: FC<EntryInstructionTimeManagementFormProps> = ({
  errors,
  entryInstructionDateTime,
  entryInstructionCombinedTime,
  skipInstructionTime,
  isValidateEntryInstructionTime,
  handleClearDriverSetting,
  handleChange,
}) => {
  // Update form data and validate field
  const handleUpdateFormData = useCallback(
    (fieldName: string, value: string) => {
      if (handleChange) {
        handleChange(value, fieldName)
      }
    },
    [handleChange]
  )

  const formConfig = useMemo(() => {
    const defaultUnassignOptions = {
      value: DEFAULT_OTHER_OPTION_SELECT,
      label: '未定',
    }

    // Generate options for time 5 minute intervals
    const defaultTimeOptions = _range(0, 24).flatMap((hour) => {
      const hourStr = hour.toString().padStart(2, '0')
      return _range(0, 60, 5).map((minute) => ({
        label: `${hourStr}:${minute.toString().padStart(2, '0')}`,
        value: `${hourStr}:${minute.toString().padStart(2, '0')}`,
      }))
    })

    // Generate time options
    const combinedTimeOptions = _uniqBy([...defaultTimeOptions], 'value')

    // Return form configuration
    return {
      formWidth: 360,
      options: {
        combinedTimes: [defaultUnassignOptions, ...combinedTimeOptions],
      },
    }
  }, [])

  return (
    <FormBlockWrapper>
      <div
        className="d-flex mb-3 flex-shrink-0 align-items-center"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <h3 data-dg-id="entry-instruction-time-management-form-title">入場指示時刻</h3>
        <div>
          <ButtonWrapper
            outline
            color="primary"
            className="bg-white ml-2 px-md-4 px-sm-1 px-0 text-primary"
            size="sm"
            style={{ whiteSpace: 'nowrap' }}
            data-dg-id="entry-instruction-time-management-form-clear-button"
            onClick={handleClearDriverSetting}
          >
            クリア
          </ButtonWrapper>
        </div>
      </div>

      <div className="d-flex">
        <SingleForm
          className="input-flex"
          id="skipInstructionTime"
          name="skipInstructionTime"
          type="checkbox"
          label="指示を受けていない"
          value={skipInstructionTime}
          onChange={(val: boolean) => {
            handleUpdateFormData('skipInstructionTime', val.toString())
            handleClearDriverSetting()
          }}
          isCheckType
          isHightLight
        />
      </div>

      <div className="d-flex">
        <SingleForm
          id="entryInstructionDateTime"
          name="entryInstructionDateTime"
          label="指示日"
          value={entryInstructionDateTime}
          onChange={(value: string) =>
            handleUpdateFormData('entryInstructionDateTime', moment(value).format('YYYY-MM-DD'))
          }
          isDayPicker
          isDisabled={skipInstructionTime}
          errorMessage={isValidateEntryInstructionTime?.entryInstructionDateTime}
        />
      </div>

      <div className="d-flex align-items-center mb-1 mt-1">
        <>
          <span
            className="label-flex"
            style={{ whiteSpace: 'nowrap' }}
            data-dg-id="entry-instruction-time-management-form-label"
          >
            指示時間
          </span>
          <TimePickerDiffTime
            id="entryInstructionCombinedTime"
            name="entryInstructionCombinedTime"
            options={formConfig.options.combinedTimes}
            selectValue={entryInstructionCombinedTime}
            onChange={(value) => handleUpdateFormData('entryInstructionCombinedTime', value)}
            hasClass={false}
            isError={isValidateEntryInstructionTime?.entryInstructionCombinedTime}
            isDisabled={skipInstructionTime}
          />
        </>
      </div>

      <div className="d-flex">
        <div style={{ width: 140 }} className="label" />
        <div className="d-flex align-items-center mb-1 mt-1">
          {errors && (
            <p
              className="text-left ml-1"
              style={{ color: `${theme.redMedium1}`, fontSize: '12px' }}
            >
              {errors.commonError}
            </p>
          )}
        </div>
      </div>
    </FormBlockWrapper>
  )
}

export default EntryInstructionTimeManagementForm
